import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import ConnectionModel, {
  HubspotAccessToken,
} from '@mm/state/model/ConnectionModel';
import { selectHubspotConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { useSelector } from 'react-redux';
import { prepareConnectionsMessage } from '../KlaviyoIntegrations/use-connect-klaviyo';
import { useGuardedConnectOAuthToken } from '@mm/stateful-components/OAuth/use-guarded-connect-o-auth-token';
import { landmineNames } from '@mm/constants/landmine-names';
import { useCallback } from 'react';
import { useHubspotCRUDActions } from '@mm/state/actions/HubspotCRUDActions';

export const useConnectHubspot = () => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const hubspotConnections = useSelector(selectHubspotConnections);
  const {
    getAccounts,
    revokeAccessToken,
    revoke: revokeToken,
    getOauthUrl,
    getLists,
    updateToken,
  } = useHubspotCRUDActions();

  const { retrieveAll, create, update, deleteConnection } =
    useConnectionCRUDActions();

  const fetchOAuthUrl = useCallback(async () => {
    const link = await getOauthUrl(facebookPageId);
    return link;
  }, [getOauthUrl]);

  const { connect: connectOauth } = useGuardedConnectOAuthToken(
    'nativeIntegrations',
    landmineNames.HUBSPOT_UPGRADE,
    false,
    'hubspotConnect',
    'hubspotTokenReceived',
    fetchOAuthUrl,
    async () => {
      const accountsResponse = await getHubspotAccounts();
      const { id } = accountsResponse[0];

      if (!hubspotConnections.length) {
        createConnection({ tokenId: id, tokenType: 'Hubspot::OauthToken' });
      } else if (
        (hubspotConnections || []).find(conn => conn.accessToken?.id !== id)
      ) {
        hubspotConnections.forEach(async connection => {
          await update(facebookPageId, {
            id: connection.id,
            hubspotParams: { tokenId: id, tokenType: 'Hubspot::OauthToken' },
          });
        });
      }
    },
    { width: 600, height: 600 },
  );

  const connectionsMessage = prepareConnectionsMessage(
    hubspotConnections,
    'Hubspot',
  );

  const createConnection = async (
    hubspotParams: ConnectionModel['hubspotParams'],
  ) =>
    create(facebookPageId, {
      name: 'Hubspot Connection',
      connectionKind: 'hubspot',
      hubspotParams,
    });

  const connectAccessToken = async (accessToken: string) => {
    const webhook = await createConnection({
      tokenType: 'Hubspot::AccessToken',
      token: { accessToken },
    });
    getHubspotConnections();
    getHubspotAccounts();
    return webhook;
  };

  const updateAccessToken = async (
    account: HubspotAccessToken,
    privateKey: string,
  ) => {
    await updateToken(facebookPageId, account.id, privateKey);
    getLists(facebookPageId, account, true);
  };

  const getHubspotAccounts = async () => getAccounts(facebookPageId);

  const getHubspotConnections = () => {
    retrieveAll(facebookPageId, {
      type: 'hubspot',
      pageSize: 200,
    });
  };

  const revoke = async (id: number) => {
    await revokeToken(id);
    hubspotConnections.forEach(async connection => {
      await deleteConnection(facebookPageId, connection as ConnectionModel);
    });
  };

  return {
    connectionsMessage,
    getHubspotConnections,
    getHubspotAccounts,
    connectOauth,
    connectAccessToken,
    revoke,
    revokeAccessToken,
    updateAccessToken,
  };
};
